import { deliverKlaviyoPurchase } from './klaviyo-purchase';

// GA4 Enhanced Ecommerce Analytics Utils
//
// TRACKING APPROACH:
// All ecommerce events push to window.dataLayer only (GTM-only).
// GTM container GTM-W4F4XCR4 forwards events to GA4, Klaviyo, etc.
// No direct window.gtag() calls — GTM is the single source of truth.

interface ProductItem {
  item_id: string;
  item_name: string;
  item_category: string;
  item_brand: string;
  price: number;
  quantity: number;
  /**
   * Item-scoped list name. GA4's Item lists report reads this from inside each
   * item, NOT from the event-level `item_list_name` parameter of the same name
   * — and the event-level one is dropped anyway, because container v44's GA4
   * event-settings variable forwards only items, value, currency, store_id,
   * store_name and dispensary_id. Set by trackViewItemList/trackSelectItem.
   */
  item_list_name?: string;
}

interface TrackViewItemProps {
  id: string;
  name: string;
  category: string;
  brand: string;
  price: number;
}

interface TrackAddToCartProps {
  id: string;
  name: string;
  category: string;
  brand: string;
  price: number;
  quantity: number;
}

interface TrackRemoveFromCartProps {
  id: string;
  name: string;
  category: string;
  brand: string;
  price: number;
  quantity: number;
}

// =============================================================================
// 🛡️ DEVELOPMENT MODE PROTECTION
// =============================================================================

/**
 * Manual override flag for testing analytics in development
 * Set this to true in console to temporarily enable tracking in dev mode:
 * window.FORCE_ANALYTICS_IN_DEV = true
 */
declare global {
  interface Window {
    FORCE_ANALYTICS_IN_DEV?: boolean;
  }
}

/**
 * Check if we're in development mode to prevent analytics pollution
 * Uses multiple detection methods for reliability
 */
const isDevelopmentMode = (): boolean => {
  // Allow manual override for testing
  if (typeof window !== 'undefined' && window.FORCE_ANALYTICS_IN_DEV) {
    return false; // Force analytics to run even in dev
  }
  // Server-side check
  if (typeof process !== 'undefined' && process.env) {
    if (process.env.NODE_ENV === 'development') return true;
  }

  // Client-side checks
  if (typeof window !== 'undefined') {
    // Check hostname for localhost/dev domains
    const hostname = window.location.hostname;
    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.includes('dev') ||
      hostname.includes('staging') ||
      hostname.includes('test')
    ) {
      return true;
    }

    // Check port for development servers
    const port = window.location.port;
    if (port === '3000' || port === '5173' || port === '8000') {
      return true;
    }
  }

  return false;
};

// Log development mode status on load
if (typeof window !== 'undefined') {
  if (isDevelopmentMode()) {
    console.log(
      '🛡️ ANALYTICS PROTECTION: Development mode detected - all tracking calls will be blocked',
    );
    console.log('💡 To test analytics in dev, run: window.FORCE_ANALYTICS_IN_DEV = true');
  }
}

/**
 * Safe console log that respects development mode
 * In production, only logs errors. In development, logs everything.
 */
const safeConsoleLog = (message: string, data?: any) => {
  if (isDevelopmentMode()) {
    console.log(message, data || '');
  }
};

/**
 * Safe console error that always logs (production and development)
 */
const safeConsoleError = (message: string, error?: any) => {
  console.error(message, error || '');
};

/**
 * Wrapper to prevent analytics calls in development mode
 * Returns early if in development, otherwise executes the tracking function
 */
const safeAnalyticsCall = (trackingFunction: () => void, eventName: string) => {
  if (isDevelopmentMode()) {
    safeConsoleLog(`🚫 DEV MODE: Blocked ${eventName} tracking call`);
    return;
  }

  try {
    trackingFunction();
  } catch (error) {
    safeConsoleError(`Error in ${eventName} tracking:`, error);
  }
};

// =============================================================================
// 🏪 STORE ATTRIBUTION HELPER
// =============================================================================

import { stores } from '~/constants/stores';

/**
 * Get store attribution data from the selectedStore cookie
 * Returns store_id, store_name, and dispensary_id for GA4 custom dimensions
 */
const getStoreAttribution = (): {
  store_id: string;
  store_name: string;
  dispensary_id: string;
} => {
  try {
    if (typeof document === 'undefined') return { store_id: '', store_name: '', dispensary_id: '' };

    const cookie = document.cookie
      .split('; ')
      .find((c) => c.startsWith('selectedStore='));
    const storeValue = cookie ? decodeURIComponent(cookie.split('=')[1]) : '';

    if (!storeValue) return { store_id: '', store_name: '', dispensary_id: '' };

    const store = stores.find((s) => s.value === storeValue);

    return {
      store_id: storeValue,
      store_name: store?.name || '',
      dispensary_id: storeValue,
    };
  } catch {
    return { store_id: '', store_name: '', dispensary_id: '' };
  }
};

interface CartItem {
  product_id: string;
  name_sanitized?: string;
  name: string;
  category: string;
  brand: string;
  price: number;
  price_sell: number;
  quantity: number;
}

/**
 * Track when a user views a product
 */
export const trackViewItem = (product: TrackViewItemProps) => {
  // 🛡️ Development mode protection
  if (isDevelopmentMode()) {
    safeConsoleLog('🚫 DEV MODE: Blocked GA4 view_item tracking', {
      item_name: product.name,
      item_id: product.id,
    });
    return;
  }

  try {
    const item: ProductItem = {
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price: product.price,
      quantity: 1,
    };

    // GTM dataLayer tracking ONLY
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: 'USD',
        value: product.price,
        items: [item],
      },
      ...getStoreAttribution(),
    });

    safeConsoleLog('🔥 GA4: view_item tracked', { item_name: product.name, item_id: product.id });
  } catch (error) {
    safeConsoleError('Error tracking view_item:', error);
  }
};

/**
 * Track when a user adds a product to cart
 */
export const trackAddToCart = (product: TrackAddToCartProps) => {
  try {
    const item: ProductItem = {
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price: product.price,
      quantity: product.quantity,
    };

    const totalValue = product.price * product.quantity;

    // GTM dataLayer tracking ONLY
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: totalValue,
        items: [item],
      },
      ...getStoreAttribution(),
    });
  } catch (error) {
    console.error('Error tracking add_to_cart:', error);
  }
};

/**
 * Track when a user begins checkout process
 */
export const trackBeginCheckout = (cartItems: CartItem[], total: number) => {
  try {
    const items: ProductItem[] = cartItems.map((item) => ({
      item_id: item.product_id,
      item_name: item.name_sanitized || item.name,
      item_category: item.category,
      item_brand: item.brand,
      price: item.price || item.price_sell,
      quantity: item.quantity,
    }));

    // GTM dataLayer tracking ONLY
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'USD',
        value: total,
        items: items,
      },
      ...getStoreAttribution(),
    });

    console.log('🔥 GA4: begin_checkout tracked', { value: total, items: items.length });
  } catch (error) {
    console.error('Error tracking begin_checkout:', error);
  }
};

/**
 * Track when a user removes a product from cart
 */
export const trackRemoveFromCart = (product: TrackRemoveFromCartProps) => {
  try {
    const item: ProductItem = {
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price: product.price,
      quantity: product.quantity,
    };

    const totalValue = product.price * product.quantity;

    // GTM dataLayer tracking ONLY
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'remove_from_cart',
      ecommerce: {
        currency: 'USD',
        value: totalValue,
        items: [item],
      },
      ...getStoreAttribution(),
    });

    console.log('🔥 GA4: remove_from_cart tracked', { value: totalValue, items: item.item_name });
  } catch (error) {
    console.error('Error tracking remove_from_cart:', error);
  }
};

/**
 * Track when a user completes a purchase
 */
export const trackPurchase = (ticketData: any) => {
  // 🛡️ Development mode protection
  if (isDevelopmentMode()) {
    safeConsoleLog('🚫 DEV MODE: Blocked GA4 purchase tracking', {
      order_number: ticketData.order_number,
      total: ticketData.total,
    });
    return;
  }

  try {
    // Transform ticket items to GA4 enhanced ecommerce format
    const ga4Items: ProductItem[] = ticketData.items.map((item: any) => ({
      item_id: item.product_id?.toString() || item.id?.toString() || 'unknown',
      item_name: item.product_size_name || item.name || 'Unknown Product',
      item_category: item.category || 'Cannabis',
      item_brand: item.brand || 'Unknown',
      price: parseFloat(item.price_sell || item.price_each || item.price || 0),
      quantity: parseInt(item.quantity || 1),
    }));

    const transactionValue = parseFloat(ticketData.total.toFixed(2));
    const taxValue = parseFloat(ticketData.tax_total.toFixed(2));

    // GTM dataLayer tracking ONLY
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: ticketData.order_number,
        value: transactionValue,
        tax: taxValue,
        shipping: 0.0,
        currency: 'USD',
        items: ga4Items,
      },
      ...getStoreAttribution(),
    });

    // 🔥 Enhanced Analytics - Add Klaviyo tracking
    klaviyo.purchase(ticketData);
  } catch (error) {
    console.error('Error tracking purchase:', error);
  }
};

/**
 * Track when a user views their cart
 */
export const trackViewCart = (cartData: { total: number; itemCount: number; items: any[] }) => {
  // 🛡️ Development mode protection
  if (isDevelopmentMode()) {
    safeConsoleLog('🚫 DEV MODE: Blocked GA4 view_cart tracking', cartData);
    return;
  }

  try {
    const items: ProductItem[] = cartData.items.map((item) => ({
      item_id: item.product_id || item.id,
      item_name: item.name || item.name_sanitized,
      item_category: item.category || 'Cannabis',
      item_brand: item.brand || 'Unknown',
      price: item.price || item.price_sell,
      quantity: item.quantity,
    }));

    // GTM dataLayer tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_cart',
      ecommerce: {
        currency: 'USD',
        value: cartData.total,
        items: items,
      },
      ...getStoreAttribution(),
    });

    safeConsoleLog('🔥 GA4: view_cart tracked', { value: cartData.total, items: items.length });
  } catch (error) {
    safeConsoleError('Error tracking view_cart:', error);
  }
};

/**
 * Track when a user searches for products
 */
export const trackSearch = (searchTerm: string) => {
  // 🛡️ Development mode protection
  if (isDevelopmentMode()) {
    safeConsoleLog('🚫 DEV MODE: Blocked GA4 search tracking', { search_term: searchTerm });
    return;
  }

  try {
    // GTM dataLayer tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'search',
      search_term: searchTerm,
    });

    safeConsoleLog('🔥 GA4: search tracked', { search_term: searchTerm });
  } catch (error) {
    safeConsoleError('Error tracking search:', error);
  }
};

/**
 * Track when a user logs in
 */
export const trackLogin = (method: string) => {
  // 🛡️ Development mode protection
  if (isDevelopmentMode()) {
    safeConsoleLog('🚫 DEV MODE: Blocked GA4 login tracking', { method: method });
    return;
  }

  try {
    // GTM dataLayer tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'login',
      method: method,
    });

    safeConsoleLog('🔥 GA4: login tracked', { method: method });
  } catch (error) {
    safeConsoleError('Error tracking login:', error);
  }
};

/**
 * Track when a user logs out
 */
export const trackLogout = () => {
  // 🛡️ Development mode protection
  if (isDevelopmentMode()) {
    safeConsoleLog('🚫 DEV MODE: Blocked GA4 logout tracking');
    return;
  }

  try {
    // GTM dataLayer tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'logout',
    });

    safeConsoleLog('🔥 GA4: logout tracked');
  } catch (error) {
    safeConsoleError('Error tracking logout:', error);
  }
};

/**
 * Track when a user signs up
 */
export const trackSignUp = (method: string) => {
  try {
    // GTM dataLayer tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'sign_up',
      method: method,
    });

    console.log('🔥 GA4: sign_up tracked', { method: method });
  } catch (error) {
    console.error('Error tracking sign_up:', error);
  }
};

/**
 * Track when a list of items is presented to the user
 */
export const trackViewItemList = (listId: string, listName: string, items: any[]) => {
  // Every other tracking function in this file guards on this; `view_item_list`
  // was the one that did not, so preview and local traffic was being pushed
  // into the production dataLayer.
  if (isDevelopmentMode()) {
    safeConsoleLog('🚫 DEV MODE: Blocked GA4 view_item_list tracking', {
      list: listName,
      items: items.length,
    });
    return;
  }

  try {
    const ga4Items: ProductItem[] = items.map((item) => ({
      item_id: item.id || item.product_id,
      item_name: item.name,
      item_category: item.category || item.category_type || 'Cannabis',
      item_brand: item.brand,
      price: item.price || item.salePrice || item.treez_price_sell,
      quantity: 1,
      item_list_name: listName,
    }));

    // GTM dataLayer tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'view_item_list',
      item_list_id: listId,
      item_list_name: listName,
      // Items must live under `ecommerce`, not at the top level: the GA4 tags
      // run with sendEcommerceData=false and read items from a Data Layer
      // Variable named `ecommerce.items`. A flat `items` array reaches GA4 as
      // nothing at all. Matches add_to_cart/purchase/view_item in this file.
      ecommerce: {
        currency: 'USD',
        items: ga4Items,
      },
      ...getStoreAttribution(),
    });

    console.log('🔥 GA4: view_item_list tracked', { list: listName, items: ga4Items.length });
  } catch (error) {
    console.error('Error tracking view_item_list:', error);
  }
};

/**
 * Track when a user selects content (categories, filters, etc.)
 */
export const trackSelectContent = (contentType: string, contentId: string) => {
  try {
    // GTM dataLayer tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'select_content',
      content_type: contentType,
      content_id: contentId,
    });

    console.log('🔥 GA4: select_content tracked', {
      content_type: contentType,
      content_id: contentId,
    });
  } catch (error) {
    console.error('Error tracking select_content:', error);
  }
};

/**
 * Track when a user selects an item from a list
 */
export const trackSelectItem = (
  product: TrackViewItemProps,
  listId?: string,
  listName?: string,
) => {
  // 🛡️ Development mode protection
  if (isDevelopmentMode()) {
    safeConsoleLog('🚫 DEV MODE: Blocked GA4 select_item tracking', {
      product: product.name,
      list: listName,
    });
    return;
  }

  try {
    const item: ProductItem = {
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price: product.price,
      quantity: 1,
      ...(listName ? { item_list_name: listName } : {}),
    };

    // GTM dataLayer tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'select_item',
      item_list_id: listId,
      item_list_name: listName,
      // Same reason as view_item_list above — `ecommerce.items` is what the
      // container reads. This event already had a working trigger and tag, so
      // it has been reaching GA4 all along with an empty items array.
      ecommerce: {
        currency: 'USD',
        items: [item],
      },
      ...getStoreAttribution(),
    });

    safeConsoleLog('🔥 GA4: select_item tracked', { product: product.name, list: listName });
  } catch (error) {
    safeConsoleError('Error tracking select_item:', error);
  }
};

// =============================================================================
// 🔥 KLAVIYO ENHANCED ECOMMERCE TRACKING
// =============================================================================

/**
 * Wait for Klaviyo to be ready before executing tracking
 * Disabled in development to prevent performance warnings
 */
const waitForKlaviyo = (callback: () => void, maxWaitTime: number = 2000) => {
  // Skip Klaviyo entirely in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[Klaviyo] Tracking disabled in development mode');
    return;
  }

  // Production only: Simple check with minimal polling
  const startTime = Date.now();

  const checkKlaviyo = () => {
    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      callback();
      return;
    }

    // Simple timeout check - only try for 2 seconds max
    if (Date.now() - startTime < maxWaitTime) {
      setTimeout(checkKlaviyo, 200); // Check every 200ms (max 10 attempts)
    }
    // No warning in production - just skip silently
  };

  checkKlaviyo();
};

/**
 * Performance monitoring for analytics (development only)
 */
const logPerformanceWarning = (operation: string, duration: number) => {
  if (process.env.NODE_ENV === 'development' && duration > 50) {
    console.warn(`[Analytics Performance] ${operation} took ${duration}ms`);
  }
};

/**
 * Centralized Klaviyo tracking functions
 * All Klaviyo events use consistent data structure and error handling
 */
export const klaviyo = {
  /**
   * Generic track method for custom events
   */
  track: (eventName: string, payload: Record<string, any>) => {
    // 🛡️ Development mode protection
    if (isDevelopmentMode()) {
      safeConsoleLog(`🚫 DEV MODE: Blocked Klaviyo ${eventName} tracking`, payload);
      return;
    }

    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track(eventName, payload);
      console.log(`[Klaviyo] ${eventName} tracked`, payload);
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user adds a product to cart
   */
  addToCart: (product: any) => {
    // 🛡️ Development mode protection
    if (isDevelopmentMode()) {
      safeConsoleLog('🚫 DEV MODE: Blocked Klaviyo Added to Cart tracking', product);
      return;
    }

    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Added to Cart', {
        ProductName: product.name,
        ProductID: product.id,
        SKU: product.id,
        Categories: [product.category],
        ImageURL: product.image || product.primary_image || '',
        URL: window.location.href,
        Brand: product.brand,
        Price: product.salePrice || product.price,
        CompareAtPrice: product.price,
      });
      console.log('[Klaviyo] Added to Cart tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user removes a product from cart
   */
  removeFromCart: (product: any) => {
    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Removed from Cart', {
        ProductName: product.name,
        ProductID: product.id,
        SKU: product.id,
        Categories: [product.category],
        ImageURL: product.image || '',
        URL: window.location.href,
        Brand: product.brand,
        Price: product.price,
        CompareAtPrice: product.price,
        Quantity: product.quantity,
      });
      console.log('[Klaviyo] Removed from Cart tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user views a product page
   */
  viewProduct: (product: any) => {
    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Viewed Product', {
        ProductName: product.name,
        ProductID: product.id,
        SKU: product.id,
        Categories: [product.category],
        ImageURL: product.image || product.primary_image || '',
        URL: window.location.href,
        Brand: product.brand,
        Price: product.salePrice || product.price,
        CompareAtPrice: product.price,
        ProductDescription: product.product_description || '',
        THC: product.thc || '',
        Classification: product.classification || '',
      });
      console.log('[Klaviyo] Viewed Product tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user views their cart
   */
  viewCart: (cartData: any) => {
    // 🛡️ Development mode protection
    if (isDevelopmentMode()) {
      safeConsoleLog('🚫 DEV MODE: Blocked Klaviyo Viewed Cart tracking', cartData);
      return;
    }

    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Viewed Cart', {
        CartValue: cartData.total,
        CartItemCount: cartData.itemCount,
        CartItems: cartData.items.map((item: any) => ({
          ProductName: item.name,
          ProductID: item.product_id,
          SKU: item.product_id,
          Brand: item.brand,
          Price: item.price,
          Quantity: item.quantity,
        })),
        URL: window.location.href,
      });
      console.log('[Klaviyo] Viewed Cart tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user starts checkout process
   */
  startCheckout: (checkoutData: any) => {
    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Started Checkout', checkoutData);
      console.log('[Klaviyo] Started Checkout tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user searches for products
   */
  search: (searchTerm: string, resultsCount: number) => {
    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Searched', {
        SearchTerm: searchTerm,
        ResultsCount: resultsCount,
        URL: window.location.href,
      });
      console.log('[Klaviyo] Searched tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user logs in
   */
  login: (method: string) => {
    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Login', {
        LoginMethod: method,
        URL: window.location.href,
      });
      console.log('[Klaviyo] Login tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user logs out
   */
  logout: () => {
    // 🛡️ Development mode protection
    if (isDevelopmentMode()) {
      safeConsoleLog('🚫 DEV MODE: Blocked Klaviyo logout tracking');
      return;
    }

    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Logout', {
        URL: window.location.href,
      });
      console.log('[Klaviyo] Logout tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Identify a user with their profile data
   */
  identify: (userData: {
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
    dob?: string;
  }) => {
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      window.klaviyo.identify(userData);
      console.log('[Klaviyo] User identified');
    } else {
      console.warn('[Klaviyo] identify method not available yet');
    }
  },

  /**
   * Track when a user completes a purchase
   */
  purchase: (ticketData: any, store = '', firstVisit = true) => {
    if (typeof window === 'undefined' || isDevelopmentMode()) return;
    // Transform ticket items to Klaviyo format
    const klaviyoItems = ticketData.items.map((item: any) => ({
      ProductName: item.product_size_name || item.name || 'Unknown Product',
      SKU: item.product_id?.toString() || item.id?.toString() || 'unknown',
      Categories: [item.category || 'Cannabis'],
      Brand: item.brand || 'Unknown',
      Price: parseFloat(item.price_sell || item.price_each || item.price || 0),
      Quantity: parseInt(item.quantity || 1),
    }));

    const payload = {
      $event_id: ticketData.order_number,
      $value: Number(Number(ticketData.total).toFixed(2)),
      OrderId: ticketData.order_number,
      Categories: ['Cannabis'],
      ItemNames: klaviyoItems.map((item: any) => item.ProductName),
      Brands: [...new Set(klaviyoItems.map((item: any) => item.Brand))],
      Items: klaviyoItems,
      BillingAddress: {
        FirstName: ticketData.customer_first_name || '',
        LastName: ticketData.customer_last_name || '',
        Country: 'US',
      },
      ShippingAddress: {
        FirstName: ticketData.customer_first_name || '',
        LastName: ticketData.customer_last_name || '',
        Country: 'US',
      },
    };
    return deliverKlaviyoPurchase(ticketData, store, firstVisit, payload);
  },

  /**
   * Track when a user selects a category
   */
  categorySelected: (categoryName: string, source: string, currentCategory?: string) => {
    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track('Category Selected', {
        category_name: categoryName,
        previous_category: currentCategory,
        source: source,
        timestamp: new Date().toISOString(),
      });
      console.log('[Klaviyo] Category Selected tracked');
    } else {
      console.warn('[Klaviyo] track method not available yet');
    }
  },

  /**
   * Track when a user views a category page (with safe waiting)
   */
  categoryPageViewed: (
    categoryName: string,
    categorySlug: string,
    productCount: number,
    page: number,
  ) => {
    waitForKlaviyo(() => {
      if (window.klaviyo && typeof window.klaviyo.track === 'function') {
        window.klaviyo.track('Category Page Viewed', {
          category_name: categoryName,
          category_slug: categorySlug,
          product_count: productCount,
          page_number: page,
          timestamp: new Date().toISOString(),
        });
        console.log('[Klaviyo] Category Page Viewed tracked');
      }
    });
  },
};

// =============================================================================
// 🎯 ENHANCED ANALYTICS - UNIFIED TRACKING
// =============================================================================

/**
 * Enhanced analytics functions that call both GA4 and Klaviyo
 * Use these for new implementations to ensure consistent dual tracking
 */
export const analytics = {
  /**
   * Track when a user clicks on a promotional carousel slide/card (GA4)
   */
  trackSelectPromotion: (params: {
    promotion_id: string;
    promotion_name: string;
    creative_slot: string;
  }) => {
    safeAnalyticsCall(() => {
      // GTM dataLayer tracking
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce object
      window.dataLayer.push({
        event: 'select_promotion',
        ecommerce: {
          promotion_id: params.promotion_id,
          promotion_name: params.promotion_name,
          creative_slot: params.creative_slot,
        },
        ...getStoreAttribution(),
      });

      safeConsoleLog('GA4: select_promotion tracked', params);
    }, 'select_promotion');
  },

  /**
   * Track product view (GA4 + Klaviyo)
   */
  trackViewItemComplete: (product: TrackViewItemProps) => {
    trackViewItem(product); // Existing GA4 tracking
    klaviyo.viewProduct(product); // New Klaviyo tracking
  },

  /**
   * Track add to cart (GA4 + Klaviyo)
   */
  trackAddToCartComplete: (product: TrackAddToCartProps) => {
    trackAddToCart(product); // Existing GA4 tracking
    klaviyo.addToCart(product); // New Klaviyo tracking
  },

  /**
   * Track remove from cart (GA4 + Klaviyo)
   */
  trackRemoveFromCartComplete: (product: TrackRemoveFromCartProps) => {
    trackRemoveFromCart(product); // Existing GA4 tracking
    klaviyo.removeFromCart(product); // New Klaviyo tracking
  },

  /**
   * Track purchase completion (GA4 + Klaviyo)
   */
  trackPurchaseComplete: (ticketData: any) => {
    trackPurchase(ticketData); // This now calls both GA4 and Klaviyo
  },

  /**
   * Track view cart (GA4 + Klaviyo)
   */
  trackViewCartComplete: (cartData: { total: number; itemCount: number; items: any[] }) => {
    trackViewCart(cartData); // New GA4 tracking
    klaviyo.viewCart(cartData); // Existing Klaviyo tracking
  },

  /**
   * Track search (GA4 + Klaviyo)
   */
  trackSearchComplete: (searchTerm: string, resultsCount: number = 0) => {
    trackSearch(searchTerm); // New GA4 tracking
    klaviyo.search(searchTerm, resultsCount); // Existing Klaviyo tracking
  },

  /**
   * Track login (GA4 + Klaviyo)
   */
  trackLoginComplete: (method: string) => {
    trackLogin(method); // New GA4 tracking
    klaviyo.login(method); // Existing Klaviyo tracking
  },

  /**
   * Track sign up (GA4 + Klaviyo)
   */
  trackSignUpComplete: (method: string) => {
    trackSignUp(method); // New GA4 tracking
    // Note: Klaviyo doesn't have a sign_up event, handled via identify()
  },

  /**
   * Track view item list (GA4 + Klaviyo)
   */
  trackViewItemListComplete: (listId: string, listName: string, items: any[]) => {
    trackViewItemList(listId, listName, items); // New GA4 tracking
    // Note: Klaviyo handles this via categoryPageViewed()
  },

  /**
   * Track select content (GA4 + Klaviyo)
   */
  trackSelectContentComplete: (contentType: string, contentId: string) => {
    trackSelectContent(contentType, contentId); // New GA4 tracking
    // Note: Klaviyo handles this via categorySelected()
  },

  /**
   * Track select item (GA4 + Klaviyo)
   */
  trackSelectItemComplete: (product: TrackViewItemProps, listId?: string, listName?: string) => {
    trackSelectItem(product, listId, listName); // New GA4 tracking
    klaviyo.viewProduct(product); // Existing Klaviyo tracking (product clicks = viewed product)
  },

  /**
   * Track logout (GA4 + Klaviyo)
   */
  trackLogoutComplete: () => {
    trackLogout(); // New GA4 tracking
    klaviyo.logout(); // New Klaviyo tracking
  },
};
